import { Link } from "react-router-dom";
import { Brand } from "@/components/Brand";
import { CrawlableLink } from "@/components/CrawlableLink";
import { SCRIPTURE_EDITION_NOTICE, SITE_AFFILIATION, PERSONAL_TESTIMONY_POLICY } from "@/data/editorial";

const COLUMNS = [
  {
    heading: "Discover",
    links: [
      { label: "Discover Mary", to: "#discover" },
      { label: "Marian Apologetics", to: "/apologetics" },
      { label: "East & West", to: "/east-and-west" },
      { label: "Sacred Art & Icons", to: "/sacred-art" },
      { label: "Saints & Witnesses", to: "/saints" },
      { label: "Library", to: "/library" },
      { label: "Christos · King of Glory", to: "/christ-the-king" },
      { label: "Dogmas of Jesus", to: "/christ-the-king/dogmas" },
    ],
  },
  {
    heading: "Pray & Visit",
    links: [
      { label: "Prayer & Devotion", to: "/prayer-and-devotion" },
      { label: "The Holy Rosary", to: "/rosary" },
      { label: "Divine Mercy Chaplet", to: "/christ-the-king/divine-mercy#chaplet" },
      { label: "The Prayer Rope", to: "/prayer-rope" },
      { label: "Totus Tuus · 33 days", to: "/christ-the-king/consecration" },
      { label: "Home Enthronement", to: "/christ-the-king/enthronement" },
      { label: "Apparitions & Shrines", to: "/apparitions" },
      { label: "Parish Poster Kit", to: "/parish-poster-kit" },
      { label: "Feasts & Calendar", to: "/calendar" },
      { label: "Today with Mary", to: "#today" },
      { label: "Leave an intention", to: "#invitation" },
    ],
  },
  {
    heading: "About",
    links: [
      { label: "About the treasury", to: "/about" },
      { label: "Editorial Standards", to: "/editorial-standards" },
      { label: "Contact", to: "/contact" },
      { label: "Privacy Policy", to: "/privacy" },
    ],
  },
];

export default function Footer() {
  return (
    <footer data-testid="main-footer" className="bg-midnight text-ivory">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-8 lg:px-16">
        <div className="grid gap-12 lg:grid-cols-[1.2fr_2fr]">
          <div>
            <Brand dark testid="footer-logo" />
            <p className="mt-6 max-w-sm font-serif text-lg italic leading-relaxed text-ivory/70">
              To Jesus through Mary — all Marian devotion finds its meaning in Christ, the one
              Saviour and the heart of the Gospel.
            </p>
            <p className="mt-5 font-mono text-[10px] uppercase tracking-[0.3em] text-ivory/40">
              Latin and Eastern Catholic · one Church, one Mother
            </p>
          </div>

          <nav className="grid grid-cols-2 gap-8 sm:grid-cols-3" aria-label="Footer">
            {COLUMNS.map((col) => (
              <div key={col.heading}>
                <h2 data-testid={`footer-heading-${col.heading.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="font-mono text-[11px] uppercase tracking-[0.28em] text-gold">
                  {col.heading}
                </h2>
                <ul className="mt-4 space-y-2.5">
                  {col.links.map((link) => (
                    <li key={link.label}>
                      <CrawlableLink
                        to={link.to}
                        data-testid={`footer-link-${link.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                        className="text-left text-sm text-ivory/70 transition-colors hover:text-ivory"
                      >
                        {link.label}
                      </CrawlableLink>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>
        </div>

        <section aria-labelledby="footer-ministry-heading" data-testid="footer-ministry-statement" className="mt-14 border-t border-ivory/10 pt-8">
          <h2 id="footer-ministry-heading" data-testid="footer-ministry-heading" className="font-mono text-[11px] uppercase tracking-[0.28em] text-gold">Ministry statement</h2>
          <p data-testid="footer-ministry-affiliation" className="mt-4 max-w-4xl text-sm leading-7 text-ivory/90">This website is part of <a href="https://thegatetoheavenministry.org/" target="_blank" rel="noopener noreferrer" data-testid="footer-ministry-affiliation-link" className="text-gold underline underline-offset-4 transition-colors hover:text-ivory focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4">The Gate to Heaven Ministry</a>.</p>
          <p data-testid="footer-ministry-testimony-attribution" className="mt-5 text-xs text-gold">Rachel’s testimony</p>
          <p data-testid="footer-ministry-original-text" className="mt-2 max-w-4xl break-words text-sm leading-7 text-ivory/80">
            This website{"\u00a0"}<a href="http://theotokosmaterdei.com" target="_blank" rel="noopener noreferrer" data-testid="footer-ministry-website-link" className="break-words text-gold underline underline-offset-4 transition-colors hover:text-ivory focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4">http://theotokosmaterdei.com</a>{"\u00a0"}Rachel asked God about this new website and HE gave HIS permission along with Rachel and <a href="https://thegatetoheavenministry.org/" target="_blank" rel="noopener noreferrer" data-testid="footer-ministry-testimony-link" className="text-gold underline underline-offset-4 transition-colors hover:text-ivory focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4">The Gate to Heaven Ministry</a> for this new website about Mother Mary written by Fred Bachour.
          </p>
          <p data-testid="footer-testimony-policy" className="mt-3 max-w-4xl text-xs leading-relaxed text-ivory/70">{PERSONAL_TESTIMONY_POLICY}</p>
        </section>

        <div className="mt-8 border-t border-ivory/10 pt-8">
          <p data-testid="footer-disclaimer" className="max-w-3xl text-xs leading-relaxed text-ivory/55">
            {SITE_AFFILIATION} Not an official website of the Holy
            See, a diocese, or any shrine unless otherwise stated. Material concerning
            apparitions and private revelations is presented for education and devotion;
            Catholics are not obliged to believe private revelations, even where the Church
            permits devotion. Sacred art and photography reproduced from public-domain,
            Creative Commons, and licensed sources with attribution.{" "}
            <Link to="/editorial-standards" className="text-gold/90 underline-offset-2 hover:underline" data-testid="footer-editorial-link">
              Read our Editorial Standards
            </Link>
            .
          </p>
          <p data-testid="footer-scripture-disclaimer" className="mt-5 max-w-3xl text-xs leading-relaxed text-ivory/70">{SCRIPTURE_EDITION_NOTICE}</p>
          <Link to="/editorial-standards#scripture" data-testid="footer-scripture-sources-link" className="mt-3 inline-block text-xs text-gold underline underline-offset-4 transition-colors hover:text-ivory">Scripture editions & quotation policy</Link>
          <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-ivory/45">
              © 2026 Theotokos · Mater Dei
            </p>
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-ivory/45">
              Editorial review September 2026 · Ad Iesum per Mariam
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
